import React, { useState, useEffect, useCallback } from 'react';
import { syncSchedules } from '../services/api';
import './SyncConfig.css';

const INTERVAL_OPTIONS = [
  { value: 0,    label: 'Désactivé' },
  { value: 5,    label: 'Toutes les 5 min' },
  { value: 15,   label: 'Toutes les 15 min' },
  { value: 30,   label: 'Toutes les 30 min' },
  { value: 60,   label: 'Toutes les heures' },
  { value: 120,  label: 'Toutes les 2h' },
  { value: 360,  label: 'Toutes les 6h' },
  { value: 720,  label: 'Toutes les 12h' },
  { value: 1440, label: 'Toutes les 24h' },
];

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function SyncConfig() {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState({});
  const [syncing, setSyncing]     = useState({});
  const [msg, setMsg]             = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await syncSchedules.getAll();
      setSchedules(res.data);
    } catch (e) {
      setMsg({ type: 'err', text: e.response?.data?.error || 'Erreur de chargement' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRun = async (key) => {
    setSyncing(prev => ({ ...prev, [key]: true }));
    setMsg(null);
    const label = schedules.find(s => s.database_key === key)?.label || key;
    try {
      const res = await syncSchedules.run(key);
      const { last_status, last_error, last_sync_at } = res.data || {};
      setSchedules(prev =>
        prev.map(s => s.database_key === key
          ? { ...s, last_status, last_error, last_sync_at }
          : s
        )
      );
      if (last_status === 'error') {
        setMsg({ type: 'err', text: `${label} — ${last_error || 'Erreur sync'}` });
      } else {
        setMsg({ type: 'ok', text: `${label} synchronisée` });
        setTimeout(() => setMsg(null), 3000);
      }
    } catch (e) {
      setMsg({ type: 'err', text: e.response?.data?.error || 'Erreur de synchronisation' });
    } finally {
      setSyncing(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleChange = async (key, interval_minutes) => {
    setSaving(prev => ({ ...prev, [key]: true }));
    setMsg(null);
    try {
      await syncSchedules.update(key, { interval_minutes });
      setSchedules(prev =>
        prev.map(s => s.database_key === key ? { ...s, interval_minutes } : s)
      );
      setMsg({ type: 'ok', text: `Périodicité mise à jour pour ${schedules.find(s => s.database_key === key)?.label}` });
      setTimeout(() => setMsg(null), 3000);
    } catch (e) {
      setMsg({ type: 'err', text: e.response?.data?.error || 'Erreur de sauvegarde' });
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  if (loading) return <div className="sync-config-loading">Chargement…</div>;

  return (
    <div className="sync-config">
      <h2 className="sync-config-title">🔄 Synchronisation automatique</h2>
      <p className="sync-config-subtitle">
        Configurez la fréquence de synchronisation automatique pour chaque base de données SFTP.<br />
        Le scheduler vérifie chaque minute si une synchronisation est nécessaire.
      </p>

      {msg && (
        <div className={`sync-config-msg sync-config-msg-${msg.type}`}>
          {msg.type === 'ok' ? '✅' : '❌'} {msg.text}
        </div>
      )}

      <div className="table-container">
        <table className="sync-config-table">
          <thead>
            <tr>
              <th>Base de données</th>
              <th>Périodicité</th>
              <th>Dernière sync</th>
              <th>Statut</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {schedules.map(s => (
              <tr key={s.database_key}>
                <td className="sync-config-label">{s.label}</td>
                <td>
                  <select
                    className="sync-config-select"
                    value={s.interval_minutes}
                    onChange={e => handleChange(s.database_key, parseInt(e.target.value, 10))}
                    disabled={!!saving[s.database_key]}
                  >
                    {INTERVAL_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </td>
                <td className="sync-config-date">{formatDate(s.last_sync_at)}</td>
                <td>
                  {s.last_status === 'success' && <span className="sync-config-status sync-config-status-ok">✅ OK</span>}
                  {s.last_status === 'error' && (
                    <span className="sync-config-status sync-config-status-err" title={s.last_error || ''}>
                      ❌ Erreur
                    </span>
                  )}
                  {!s.last_status && <span className="sync-config-status sync-config-status-none">—</span>}
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn-icon"
                      title="Synchroniser maintenant"
                      onClick={() => handleRun(s.database_key)}
                      disabled={!!syncing[s.database_key]}
                    >
                      {syncing[s.database_key] ? '⏳' : '🔄'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
